import {
	Modal,
	Button,
	Form,
	Input,
	Checkbox,
	Spin,
	InputNumber,
	Select,
	Tooltip
} from 'antd';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faWandMagicSparkles,
	faGlobe,
	faShieldCheck
} from '@fortawesome/pro-light-svg-icons';
import { ScenarioHorizon, HorizonUnit } from 'services/situationAnalysisService';
import { AI_GENERATION_ENABLED } from 'config/featureFlags';
import { AttachmentsSection } from 'components/AttachmentsSection';
import './ScenarioEditionModal.sass';

const formLayout = {
	labelCol: { span: 24 },
	wrapperCol: { span: 24 }
};

export interface ScenarioEditionValues {
	name: string;
	description?: string;
	impacts?: string;
	// V2 AI generation flow
	aiGenerate?: boolean;
	hypothesis?: string;
	horizon?: ScenarioHorizon;
}

const DEFAULT_HORIZON: ScenarioHorizon = { value: 12, unit: 'm' };

export const ScenarioEditionModal = (props: any) => {
	const [form] = Form.useForm();
	const { t } = useTranslation();
	const [isCloning, setIsCloning] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [aiGenerate, setAiGenerate] = useState(false);
	const isCreation = props.id === false;

	useEffect(() => {
		setAiGenerate(false);
	}, [props.values]);

	const renderPrivateChip = () => (
		<Tooltip
			color="rgba(183, 235, 143, 0.92)"
			overlayInnerStyle={{ color: '#1f4d0a' }}
			overlayClassName="ai-zone-tooltip private"
			title={t('messages.ai_project_generation.field_private_tooltip')}
		>
			<span className="ai-zone-label private xs">
				<FontAwesomeIcon icon={faShieldCheck as any} className="ai-zone-icon" />
				{t('messages.ai_project_generation.field_private_chip')}
			</span>
		</Tooltip>
	);

	const renderPublicChip = () => (
		<Tooltip
			color="rgba(255, 192, 130, 0.92)"
			overlayInnerStyle={{ color: '#5a2e08' }}
			overlayClassName="ai-zone-tooltip public"
			title={t('models.scenario.ai.zone_public_tooltip')}
		>
			<span className="ai-zone-label public xs">
				<FontAwesomeIcon icon={faGlobe as any} className="ai-zone-icon" />
				{t('messages.ai_project_generation.zone_public')}
			</span>
		</Tooltip>
	);

	const renderPrivateLabel = (label: string) => (
		<span className="ai-label-with-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
			<span>{label}</span>
			{aiGenerate && renderPrivateChip()}
		</span>
	);

	const handleFormSubmit = async () => {
		try {
			const values = await form.validateFields();
			setIsSaving(true);
			try {
				const payload: any = {
					...values,
					aiGenerate: aiGenerate && isCreation,
					hypothesis: aiGenerate ? values.hypothesis : undefined,
					horizon: aiGenerate
						? {
							value: values.horizonValue ?? DEFAULT_HORIZON.value,
							unit: values.horizonUnit ?? DEFAULT_HORIZON.unit
						}
						: undefined
				};
				delete payload.horizonValue;
				delete payload.horizonUnit;
				delete payload.scope;
				delete payload.focusAxes;
				await props.onSave(payload);
			} finally {
				setIsSaving(false);
			}
		} catch (errorInfo) {
			console.log('error', errorInfo);
		}
	};

	const footer = (
		<div style={{ display: 'flex' }}>
			{props.onDelete && props.id && (
				<Button
					onClick={() => {
						props.onDelete(props);
					}}
					danger={true}
					disabled={isSaving}
					style={{ marginRight: 'auto' }}
				>
					{t('actions.delete')}
				</Button>
			)}

			{props.onPromoteAsNewBaseline && props.id && (
				<Button
					onClick={() => {
						setIsCloning(true);
						props.onPromoteAsNewBaseline(props, () => { setIsCloning(false); });
					}}
					loading={isCloning}
					disabled={isSaving}
					danger={true}
					style={{ marginRight: 'auto' }}
				>
					{t('models.scenario.actions.save_as_new_baseline')}
				</Button>
			)}
			<Button onClick={() => props.onHide()} disabled={isSaving} style={{ marginRight: 'auto' }}>
				{t('actions.close')}
			</Button>
			<Button
				onClick={handleFormSubmit}
				type="primary"
				loading={isSaving}
				icon={aiGenerate ? <FontAwesomeIcon icon={faWandMagicSparkles as any} /> : undefined}
			>
				{aiGenerate ? t('models.scenario.actions.continue_with_ai') : t('actions.save')}
			</Button>
		</div>
	);

	return (
		<Modal
			open={true}
			className="ScenarioEditionModal"
			title={props.id !== false ? t('models.scenario.actions.edit') : t('models.scenario.actions.create_new')}
			okButtonProps={{ style: { display: 'none' } }}
			onCancel={props.onHide}
			cancelText={t('actions.close')}
			footer={footer}
			maskClosable={false}
			closable={!isSaving}
			width={820}
		>
			<Spin
				spinning={isSaving}
				tip={t('models.scenario.messages.creation_in_progress')}
			>
				<Form
					{...formLayout}
					form={form}
					onFinish={(values) => {
						props.onChange(props.id, values);
						props.onHide();
					}}
					disabled={isCloning || isSaving}
					initialValues={{
						...props.values,
						horizonValue: DEFAULT_HORIZON.value,
						horizonUnit: DEFAULT_HORIZON.unit
					}}
				>
					<Form.Item
						name="name"
						label={renderPrivateLabel(t('models.scenario.fields.name'))}
						required={true}
						rules={[
							{ max: 120, message: t('messages.form.max_length_exceeded_error', { length: 120 }) },
							{ required: true, message: t('messages.form.required_error') }
						]}
					>
						<Input
							autoComplete="off"
							disabled={props.id !== null ? false : true}
							count={{ show: true, max: 120 }}
						/>
					</Form.Item>

					<Form.Item
						name="description"
						label={renderPrivateLabel(t('models.scenario.fields.description'))}
						rules={[{ max: 2000, message: t('messages.form.max_length_exceeded_error', { length: 2000 }) }]}
					>
						<Input.TextArea rows={5} count={{ show: true, max: 2000 }} />
					</Form.Item>

					<Form.Item
						name="impacts"
						label={renderPrivateLabel(t('models.scenario.fields.impacts'))}
						rules={[{ max: 4000, message: t('messages.form.max_length_exceeded_error', { length: 4000 }) }]}
					>
						<Input.TextArea rows={7} count={{ show: true, max: 4000 }} />
					</Form.Item>

					{isCreation && AI_GENERATION_ENABLED && (
						<>
							<Form.Item className="ai-generate-section">
								<Checkbox
									checked={aiGenerate}
									onChange={(e) => setAiGenerate(e.target.checked)}
								>
									<span className="ai-generate-inner">
										<span className="ai-generate-icon-wrapper">
											<FontAwesomeIcon icon={faWandMagicSparkles as any} />
										</span>
										<span className="ai-generate-text">
											<span className="ai-generate-title">{t('models.scenario.ai.toggle')}</span>
											<span className="ai-generate-subtitle">{t('models.scenario.ai.toggle_subtitle')}</span>
										</span>
									</span>
								</Checkbox>
							</Form.Item>

							{aiGenerate && (
								<>
									<Form.Item
										name="hypothesis"
										label={renderPrivateLabel(t('models.scenario.ai.hypothesis_label'))}
										rules={[
											{ required: true, message: t('messages.form.required_error') },
											{ max: 1000, message: t('messages.form.max_length_exceeded_error', { length: 1000 }) }
										]}
									>
										<Input.TextArea
											rows={4}
											placeholder={t('models.scenario.ai.hypothesis_placeholder')}
											count={{ show: true, max: 1000 }}
										/>
									</Form.Item>

									<div style={{ display: 'flex', gap: 8, marginBottom: '16px' }}>
										<Form.Item
											name="horizonValue"
											label={t('models.scenario.ai.horizon_label')}
											rules={[{ required: true, message: t('messages.form.required_error') }]}
											style={{ flex: 1 }}
										>
											<InputNumber min={1} max={120} style={{ width: '100%' }} />
										</Form.Item>
										<Form.Item
											name="horizonUnit"
											label={t('models.scenario.ai.horizon_unit_label')}
											rules={[{ required: true, message: t('messages.form.required_error') }]}
											style={{ flex: 1 }}
										>
											<Select
												options={[
													{ value: 'd', label: t('models.scenario.ai.unit.days') },
													{ value: 'w', label: t('models.scenario.ai.unit.weeks') },
													{ value: 'm', label: t('models.scenario.ai.unit.months') },
													{ value: 'y', label: t('models.scenario.ai.unit.years') }
												] as Array<{ value: HorizonUnit; label: string }>}
											/>
										</Form.Item>
									</div>

									<div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
										{renderPublicChip()}
										<span style={{ marginLeft: 8 }}>
											{t('models.scenario.ai.public_note')}
										</span>
									</div>

									{props.projectId && (
										<div style={{ marginTop: 16 }}>
											<AttachmentsSection projectId={props.projectId} />
										</div>
									)}
								</>
							)}
						</>
					)}
				</Form>
			</Spin>
		</Modal>
	);
};

export default ScenarioEditionModal;
